import React, {
  Children,
  ComponentProps,
  memo,
  RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { useLayerManager } from '../../layout'
import { ButtonSize } from '../services/ButtonService'

import { Box } from './Box'

const SPACE = 8
const DEFAULT_OFFSET = 48 + SPACE
const MINI_OFFSET = 32 + SPACE

const getXPosition = (
  alignMenu: MenuAlignment,
  left: number,
  right: number,
  insidePortal?: boolean,
) => {
  if (alignMenu === 'left') {
    return {
      left: insidePortal ? `${left}px` : '0',
    }
  }

  return {
    right: insidePortal ? `${right}px` : '0',
  }
}

const getYPosition = (size: ButtonSize, top: number) => {
  return {
    top: top + (size === 'mini' ? MINI_OFFSET : DEFAULT_OFFSET),
  }
}

const StyledMenu = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'columnCount',
})<{ columnCount?: number }>`
  ${({ columnCount }) =>
    columnCount &&
    css`
      column-count: ${columnCount};
    `};
`

export type MenuAlignment = 'left' | 'right'

interface DropdownButtonMenuProps extends ComponentProps<typeof StyledMenu> {
  onClose: () => void
  alignMenu: MenuAlignment
  size: ButtonSize
  insidePortal?: boolean
  buttonRef: RefObject<HTMLButtonElement | null>
}

export const DropdownButtonMenu = memo(
  ({
    id,
    children,
    onClose,
    alignMenu,
    size,
    'aria-label': ariaLabel,
    insidePortal,
    buttonRef,
    ...rest
  }: DropdownButtonMenuProps) => {
    const { zIndex, increaseBy } = useLayerManager()
    const [{ top, left, right }, setPosition] = useState({
      top: 0,
      left: 0,
      right: 0,
    })
    const [isCalculated, setIsCalculated] = useState(false)

    useEffect(() => {
      if (buttonRef.current && insidePortal) {
        const rect = buttonRef.current.getBoundingClientRect()

        setPosition({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          right: window.innerWidth - rect.right - window.scrollX,
        })
      }

      setIsCalculated(true)
    }, [alignMenu, buttonRef, insidePortal])

    const component = useMemo(() => {
      // to prevent menu from jumping
      if (!isCalculated) {
        return null
      }

      return (
        <StyledMenu
          {...{
            id,
            role: 'menu',
            zi: zIndex + increaseBy / 2,
            'aria-label': ariaLabel,
            position: 'absolute',
            ta: 'left',
            w: 'max-content',
            miw: insidePortal ? 'max-content' : '100%',
            bgc: 'white.100',
            bra: 'sm',
            bd: '1px solid',
            bc: 'navy.25',
            py: Children.count(children) === 1 ? 'xs' : 'sm',
            ...getYPosition(size, top),
            ...getXPosition(alignMenu, left, right, insidePortal),
            ...rest,
          }}>
          {children}
        </StyledMenu>
      )
    }, [
      alignMenu,
      ariaLabel,
      children,
      id,
      increaseBy,
      insidePortal,
      isCalculated,
      left,
      right,
      rest,
      size,
      top,
      zIndex,
    ])

    if (insidePortal) {
      return createPortal(component, document.body)
    }

    return component
  },
)
